import { FC } from 'react';
import { ValidationValueMessage } from 'react-hook-form';

import { useTranslations } from '~src/commons/utils/translations/translate';
import { useGlobalState } from '~src/global-state';

import { getStandardFieldLabel } from '~address-form/config/standard-field-label';
import { PhoneNumberInput } from '~address-form/inputs/phone-number-input/phone-number-input';
import { StandardInput } from '~address-form/inputs/standard-input';
import {
  getFieldShouldBeDisabled,
  useValidationRules,
} from '~address-form/validation/field-validation';

import { getTooltipMessage } from '../config/labels';
import { getStaticInputProps } from '../config/static-props';
import { useAddressFormBusinessLogic } from '../context/use-address-form';
import { CountryInput } from '../inputs/country-input';
import { InputElementExtension } from '../inputs/input-element-extension';
import { PhoneNumberInputV2 } from '../inputs/phone-number-input/phone-number-input-v2';
import { PostalCodeInput } from '../inputs/postal-code-input';
import { RegionInput } from '../inputs/region-input';
import { SubregionInput } from '../inputs/subregion-input';
import { VatInput } from '../inputs/vat-input/vat-input';
import { FieldID } from '../types';

interface DynamicFieldRendererProps {
  fieldId: FieldID;
  fieldType?: string;
  warning?: boolean;
}

export const DynamicFieldRenderer: FC<DynamicFieldRendererProps> = ({
  fieldId,
  warning,
  fieldType,
}) => {
  const globalState = useGlobalState();
  const { tICU } = useTranslations();
  const { flags } = globalState.sessions[0];

  const { addressFormFeatures, addressPrefill, addressFormValidationConfig } =
    useAddressFormBusinessLogic();
  const validationRules = useValidationRules(fieldId, addressFormValidationConfig);
  const label = getStandardFieldLabel(fieldId);
  const optionalLabel = `(${tICU('widget.delivery_address.OPTIONAL')})`;
  const fieldLabel = `${tICU(label)} ${
    (validationRules.required as ValidationValueMessage<boolean>).value ? '' : optionalLabel
  }`;

  const tooltipText = tICU(getTooltipMessage(fieldId));
  const staticProps = getStaticInputProps(fieldId);

  const isDisabled = getFieldShouldBeDisabled(
    fieldId,
    addressPrefill,
    addressFormFeatures.frozen_fields_after_prefill
  );

  switch (fieldId) {
    case 'street_number':
    case 'street':
    case 'city':
    case 'first_name':
    case 'last_name':
    case 'company_name':
    case 'care_of':
    case 'customs_number':
      return (
        <StandardInput
          name={fieldId}
          label={fieldLabel}
          warning={warning}
          validationRules={validationRules}
          {...staticProps}
        />
      );
    case 'address_lines':
    case 'name':
      return (
        <StandardInput
          name={fieldId}
          label={fieldLabel}
          warning={warning}
          validationRules={validationRules}
          rightElement={props => <InputElementExtension {...props} showClearButton />}
          {...staticProps}
        />
      );
    case 'email':
      return (
        <StandardInput
          name={fieldId}
          label={fieldLabel}
          warning={warning}
          validationRules={validationRules}
          rightElement={props => <InputElementExtension {...props} tooltipText={tooltipText} />}
          isDisabled={isDisabled}
          {...staticProps}
        />
      );
    case 'apartment_number':
      return (
        <StandardInput
          name={fieldId}
          label={fieldLabel}
          warning={warning}
          validationRules={validationRules}
          rightElement={props => <InputElementExtension {...props} tooltipText={tooltipText} />}
          {...staticProps}
        />
      );
    case 'postal_code':
      return (
        <PostalCodeInput
          name="postal_code"
          label={fieldLabel}
          warning={warning}
          validationRules={validationRules}
          {...staticProps}
        />
      );
    case 'region':
      return (
        <RegionInput
          label={fieldLabel}
          validationRules={validationRules}
          fieldType={fieldType}
          {...staticProps}
        />
      );
    case 'subregion':
      return (
        <SubregionInput
          label={fieldLabel}
          validationRules={validationRules}
          fieldType={fieldType}
          {...staticProps}
        />
      );
    case 'phone_number':
      return flags.enable_new_phone_number_input ? (
        <PhoneNumberInputV2 label={fieldLabel} validationRules={validationRules} {...staticProps} />
      ) : (
        <PhoneNumberInput label={fieldLabel} validationRules={validationRules} {...staticProps} />
      );
    case 'vat':
      return (
        <VatInput
          label={fieldLabel}
          warning={warning}
          validationRules={validationRules}
          {...staticProps}
        />
      );
    case 'national_id_number':
    case 'floor_number':
      return (
        <StandardInput
          name={fieldId}
          type="tel"
          label={fieldLabel}
          validationRules={validationRules}
          {...staticProps}
        />
      );
    case 'country':
      return <CountryInput countryName={globalState.purchase_country} />;

    default:
      return null;
  }
};
